import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { isNativeCapacitorApp } from '@/integrations/clients/carAuction/nativeAuth';
import { BileniaBrowser } from '@/lib/native/bileniaBrowser';
import { parseExternalUrlToAppRoute } from '@/lib/native/parseAppUrl';
import { stashPendingBankIdReturn } from '@/lib/native/pendingBankIdReturn';

const NATIVE_CALLBACK_SCHEME = 'se.bilenia.app';

/**
 * Apply an auth callback URL (custom scheme) into the HashRouter.
 * Used after ASWebAuthenticationSession resolves on iOS.
 */
export function applyNativeAuthCallbackUrl(callbackUrl: string): boolean {
  const route = parseExternalUrlToAppRoute(callbackUrl);
  if (!route) {
    console.warn('[bankIdBrowser] could not parse auth callback', callbackUrl);
    return false;
  }
  const qIndex = route.indexOf('?');
  const pathPart = qIndex >= 0 ? route.slice(0, qIndex) : route;
  const queryPart = qIndex >= 0 ? route.slice(qIndex + 1) : '';
  const path = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
  const search = queryPart ? `?${queryPart}` : '';

  // KYC result: stay on the current screen; useIdVerification refetches status
  // and shows the verified/declined toast.
  if (path.startsWith('/kyc/success')) {
    return true;
  }

  if (path.startsWith('/auth') && search) {
    stashPendingBankIdReturn(search);
  }

  // HashRouter listens to hash changes; replace avoids Back replaying BankID.
  const nextHash = `#${path}${search}`;
  if (window.location.hash === nextHash) {
    // Force remount/effect if identical (rare).
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    window.location.replace(`${window.location.pathname}${window.location.search}${nextHash}`);
  }
  return true;
}

/**
 * Open BankID / Idura so the OIDC session survives BankID app handoff.
 *
 * - iOS: ASWebAuthenticationSession (system auth sheet). Captures
 *   se.bilenia.app:// callback — no Safari, no “Öppna i…?” prompt.
 * - Android: Custom Tabs (Capacitor Browser) — unchanged.
 * - Web: full-page navigation.
 */
export async function openBankIdInSystemBrowser(absoluteUrl: string): Promise<void> {
  if (!isNativeCapacitorApp()) {
    window.location.replace(absoluteUrl);
    return;
  }

  if (Capacitor.getPlatform() === 'ios') {
    try {
      const result = await BileniaBrowser.startAuthSession({
        url: absoluteUrl,
        callbackScheme: NATIVE_CALLBACK_SCHEME,
      });
      if (result?.url) {
        applyNativeAuthCallbackUrl(result.url);
      }
      return;
    } catch (err: unknown) {
      const code =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code?: string }).code || '')
          : '';
      if (code === 'CANCELED') {
        console.info('[bankIdBrowser] ASWebAuthenticationSession canceled by user');
        return;
      }
      console.warn('[bankIdBrowser] ASWebAuthenticationSession failed', err);
      throw err;
    }
  }

  await Browser.open({ url: absoluteUrl });
}

/**
 * Best-effort close of Custom Tabs / SFSafariViewController.
 * Must never hang: on Android the tab is often already gone after the deep-link
 * return, and an awaited Browser.close() can block forever — which prevents
 * nativeCode exchange and leaves the user logged out despite a successful BankID.
 * On iOS ASWebAuthenticationSession dismisses itself when the callback arrives.
 */
export async function closeBankIdSystemBrowser(): Promise<void> {
  if (!isNativeCapacitorApp()) return;
  if (Capacitor.getPlatform() === 'ios') {
    try {
      await BileniaBrowser.cancelAuthSession();
    } catch {
      /* nothing open */
    }
    return;
  }
  try {
    await Promise.race([
      Browser.close().then(() => undefined),
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, 400);
      }),
    ]);
  } catch {
    /* already closed */
  }
}

export function resolveApiAbsoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const apiBase = (
    import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api/v1'
  ).replace(/\/$/, '');
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${apiBase}${path}`;
}
